/**
 * Word documents and PDFs dropped on a tool page (SEO_BRIEF_06 §14, /word-to-srt and /pdf-to-srt).
 *
 * A script or a transcript kept as a .docx or a .pdf is read here, in the browser, into the text
 * the TXT reader takes, and the page then parses that text as TXT exactly as it would a dropped
 * .txt. Nothing is uploaded.
 *
 * RECOGNISED BY ITS BYTES, never by its name: a ZIP whose entries include `word/document.xml` is a
 * Word document, whatever it is called, and a file whose first kilobyte holds `%PDF-` is a PDF. A
 * .docx that is really a renamed SRT goes on being read as the SRT it is, and a Word document
 * saved as .zip is still read as Word rather than unpacked as an archive of XML.
 *
 *   Word   unzipped with the site's own ZIP reader (src/worker/adapter/zip.ts), which inflates
 *          only the body part, and read by `src/formats/document/docx-text.ts`.
 *   PDF    read by pdf.js (src/pdf/read.ts, src/pdf/assets.ts).
 *
 * Then both go through `src/formats/document/text.ts`, which tidies the lines and, for prose, cuts
 * paragraphs to subtitle size, and `read.ts`, which gives the verdict.
 *
 * ONLY THE RECOGNISING IS HERE. Reading is in two bundles of its own, imported on the first drop of
 * each kind and never on a page load: `/static/build/documents/word.js` (the XML reader and the
 * prose cutting, about 20 KB) and `/static/build/documents/pdf.js` (that and pdf.js). Every tool
 * page carries this file, and most visitors never drop either.
 *
 * A PDF with no text in it (a scan: each page a picture) is REFUSED with its own reason, never
 * handed on as an empty file.
 */

import { isWordPackage } from '../../../../../src/formats/document/docx-text.ts';
import type { DocumentRead } from '../../../../../src/formats/document/read.ts';
import type { DocumentKind } from '../../../../../src/formats/document/text.ts';
import { isZip, zipEntryNames } from '../../../../../src/worker/adapter/zip.ts';

export type { DocumentKind, DocumentRead };

/** A document the page can read, with its bytes, so the file is read from disk once. */
export interface DroppedDocument {
  readonly kind: DocumentKind;
  readonly bytes: Uint8Array;
}

/**
 * `%PDF-` and a version digit within the first kilobyte, where every reader looks for it: at the
 * very start, as nearly every PDF has it, or after a line break or some binary junk, which a
 * damaged download can put in front. Not after plain text, so a subtitle file that mentions
 * "%PDF-1.4" in its first lines stays a subtitle file.
 */
export function isPdf(head: Uint8Array): boolean {
  const limit = Math.min(head.length, 1024) - 6;
  for (let i = 0; i <= limit; i++) {
    const b = head[i] as number;
    if (signatureAt(head, i)) return i === 0 || head[i - 1] === 0x0a || head[i - 1] === 0x0d;
    // Printable ASCII and whitespace are text; a control or high byte is junk, and after junk
    // the signature counts wherever it is.
    const isText = (b >= 0x20 && b < 0x7f) || b === 0x09 || b === 0x0a || b === 0x0d;
    if (!isText) {
      for (let j = i + 1; j <= limit; j++) if (signatureAt(head, j)) return true;
      return false;
    }
  }
  return false;
}

/** `%PDF-` and a digit at `i`. */
function signatureAt(head: Uint8Array, i: number): boolean {
  const digit = head[i + 5] as number;
  return (
    head[i] === 0x25 &&
    head[i + 1] === 0x50 &&
    head[i + 2] === 0x44 &&
    head[i + 3] === 0x46 &&
    head[i + 4] === 0x2d &&
    digit >= 0x30 &&
    digit <= 0x39
  );
}

/** A name the drop zone does not route (a .docx or .pdf), whose bytes say what the file is. */
export function isDocumentName(name: string): boolean {
  return /\.(docx|pdf)$/i.test(name);
}

/**
 * The document `file` is, from its bytes, or null for anything else. Reads only the first
 * kilobyte unless that is a ZIP, whose entry list is at the end.
 */
export async function sniffDocument(file: Blob): Promise<DroppedDocument | null> {
  const head = new Uint8Array(await file.slice(0, 1024).arrayBuffer());
  if (isZip(head)) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    return isWordPackage(zipEntryNames(bytes)) ? { kind: 'docx', bytes } : null;
  }
  if (isPdf(head)) return { kind: 'pdf', bytes: new Uint8Array(await file.arrayBuffer()) };
  return null;
}

export interface ReadOptions {
  /** `<meta name="build-id">`, for the `?v=` on the bundles and on pdf.js's worker. */
  readonly buildId: string | null;
  /** True once a newer drop has replaced this one: a long PDF stops between pages. */
  readonly cancelled?: () => boolean;
  /**
   * Where a reader comes from: `loadReader` on a page. The tests pass the Word bundle's source
   * and a PDF reader on pdf.js's Node build, so everything after the import is the page's code.
   */
  readonly load?: (kind: DocumentKind, buildId: string | null) => Promise<DocumentReader>;
}

/** What each reader bundle exports (web/static/documents/src/word.ts and pdf.ts). */
export interface DocumentReader {
  read(bytes: Uint8Array, buildId: string | null, cancelled?: () => boolean): Promise<DocumentRead>;
}

/** The document's text, read on this device, or the reason it has none. */
export async function readDocument(
  doc: DroppedDocument,
  options: ReadOptions,
): Promise<DocumentRead> {
  const reader = await (options.load ?? loadReader)(doc.kind, options.buildId);
  return reader.read(doc.bytes, options.buildId, options.cancelled);
}

const readers = new Map<DocumentKind, Promise<DocumentReader>>();

/** A reader's bundle, imported once per page and only when its kind of document arrives. */
export function loadReader(kind: DocumentKind, buildId: string | null): Promise<DocumentReader> {
  const known = readers.get(kind);
  if (known) return known;
  const v = buildId ? `?v=${encodeURIComponent(buildId)}` : '';
  // Literal URLs, so esbuild leaves both out of this bundle and the asset guard can see them.
  const url =
    kind === 'pdf' ? `/static/build/documents/pdf.js${v}` : `/static/build/documents/word.js${v}`;
  const loading = import(/* a URL, not bundled */ url) as Promise<DocumentReader>;
  readers.set(kind, loading);
  loading.catch(() => readers.delete(kind));
  return loading;
}
